import { useEffect, useState } from 'react';
import type { Contact, VavClient } from '@vav/client-core';
import { Avatar } from './Avatar.tsx';
import { SafetyNumberDialog } from './SafetyNumberDialog.tsx';
import { useT } from '../i18n.tsx';

/**
 * Who this person is: their picture full-circle, their name, their handle, and
 * the one security control that matters between two people — the safety
 * number. Read-only by nature: their profile is theirs.
 */
export function ContactDialog({
  client,
  contact,
  online,
  onClose,
}: {
  client: VavClient;
  contact: Contact;
  online?: boolean;
  onClose: () => void;
}) {
  const t = useT();
  const [showSafety, setShowSafety] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  async function deleteContact() {
    setBusy(true);
    try {
      await client.deleteContact(contact.id);
      onClose();
    } catch {
      setBusy(false);
    }
  }

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="overlay" onClick={onClose}>
      <div
        className="card dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t('contact.title')}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="dialog__header">
          <h2>{t('contact.title')}</h2>
          <button className="icon" onClick={onClose} aria-label={t('profile.name.cancel')}>
            ✕
          </button>
        </header>

        <div className="contact__hero">
          <Avatar name={contact.displayName} size={96} avatar={contact.avatar} client={client} online={online} />
          <strong>{contact.displayName}</strong>
          <span className="muted" dir="ltr">
            @{contact.username}
          </span>
          {contact.verified && <span className="contact__verified">✔︎ {t('contact.verified')}</span>}
        </div>

        <button onClick={() => setShowSafety(true)}>🛡️ {t('thread.verifySafety')}</button>

        {/* Local un-knowing: the conversation and its history leave this
            device; nothing is sent, nobody is told. */}
        {!confirmDelete ? (
          <button className="danger" onClick={() => setConfirmDelete(true)}>
            🗑️ {t('contact.delete')}
          </button>
        ) : (
          <div className="profile__deleteConfirm">
            <p className="hint">{t('contact.delete.explain')}</p>
            <span className="profile__deleteActions">
              <button className="danger" disabled={busy} onClick={() => void deleteContact()}>
                {t('contact.delete')}
              </button>
              <button onClick={() => setConfirmDelete(false)}>{t('profile.name.cancel')}</button>
            </span>
          </div>
        )}

        {showSafety && (
          <SafetyNumberDialog
            client={client}
            userId={contact.id}
            name={contact.displayName}
            onClose={() => setShowSafety(false)}
          />
        )}
      </div>
    </div>
  );
}
